import { useEffect, useState } from 'react';
import api from '@/api/axios';
import { ChartLineUp, CircleNotch } from '@phosphor-icons/react';

const fmtQ = (n) => `Q ${Number(n || 0).toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const firstDay = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };
const today = () => new Date().toISOString().slice(0, 10);

export const MisVentas = () => {
  const [f, setF] = useState({ desde: firstDay(), hasta: today() });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [vista, setVista] = useState('erp');

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/harperp/mis-ventas?fecha_desde=${f.desde}&fecha_hasta=${f.hasta}`, { timeout: 120000 });
      setData(r.data);
    } catch { /* silencioso */ }
    setLoading(false);
  };
  useEffect(() => { fetchData(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const detalle = vista === 'erp' ? (data?.erp?.detalle || []) : (data?.crm?.detalle || []);

  return (
    <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[14px] p-4 mb-4" data-testid="mis-ventas-card">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <h2 className="text-sm font-black flex items-center gap-1.5">
            <ChartLineUp size={16} weight="fill" className="text-emerald-500" /> Mis ventas · CRM vs HarpERP
          </h2>
          <p className="text-[10px] text-[var(--text-muted)]">Solo tus ventas — histórico por fechas</p>
        </div>
        <div className="flex gap-2 items-end flex-wrap">
          <input type="date" value={f.desde} onChange={(e) => setF({ ...f, desde: e.target.value })}
            className="bg-[var(--surface-muted)] border border-[var(--border)] rounded-md px-2 py-1.5 text-xs" data-testid="mis-ventas-desde" />
          <input type="date" value={f.hasta} onChange={(e) => setF({ ...f, hasta: e.target.value })}
            className="bg-[var(--surface-muted)] border border-[var(--border)] rounded-md px-2 py-1.5 text-xs" data-testid="mis-ventas-hasta" />
          <button onClick={fetchData} disabled={loading}
            className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-1.5 rounded-md disabled:opacity-50" data-testid="mis-ventas-consultar">
            {loading ? <CircleNotch size={13} className="animate-spin inline" /> : 'Consultar'}
          </button>
        </div>
      </div>

      {loading && !data ? (
        <p className="text-center py-4 text-xs text-[var(--text-muted)]"><CircleNotch size={15} className="animate-spin inline mr-1" /> Consultando…</p>
      ) : data && (
        <>
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div className="border border-blue-500/30 bg-blue-500/5 rounded-[10px] p-2.5 text-center">
              <p className="text-[9px] uppercase font-bold text-blue-600">Reporté en CRM</p>
              <p className="text-base font-black" data-testid="mis-ventas-crm-total">{fmtQ(data.crm.total)}</p>
              <p className="text-[9px] text-[var(--text-muted)]">{data.crm.ventas} ventas</p>
            </div>
            <div className="border border-emerald-500/30 bg-emerald-500/5 rounded-[10px] p-2.5 text-center">
              <p className="text-[9px] uppercase font-bold text-emerald-600">Facturado HarpERP</p>
              <p className="text-base font-black" data-testid="mis-ventas-erp-total">{fmtQ(data.erp.total)}</p>
              <p className="text-[9px] text-[var(--text-muted)]">{data.erp.documentos} docs</p>
            </div>
            <div className="border border-[var(--border)] bg-[var(--surface-muted)] rounded-[10px] p-2.5 text-center">
              <p className="text-[9px] uppercase font-bold text-[var(--text-muted)]">% capturado en CRM</p>
              <p className={`text-base font-black ${data.pct_capturado === null ? '' : data.pct_capturado >= 80 ? 'text-emerald-500' : data.pct_capturado >= 40 ? 'text-amber-500' : 'text-red-500'}`}>
                {data.pct_capturado === null ? '—' : `${data.pct_capturado}%`}
              </p>
            </div>
          </div>

          <div className="flex gap-1 mb-2">
            <button onClick={() => setVista('erp')} className={`px-3 py-1 text-[10px] font-bold rounded ${vista === 'erp' ? 'bg-emerald-600 text-white' : 'bg-[var(--surface-muted)] text-[var(--text-muted)]'}`} data-testid="vista-mis-erp">HarpERP ({data.erp.documentos})</button>
            <button onClick={() => setVista('crm')} className={`px-3 py-1 text-[10px] font-bold rounded ${vista === 'crm' ? 'bg-blue-600 text-white' : 'bg-[var(--surface-muted)] text-[var(--text-muted)]'}`} data-testid="vista-mis-crm">CRM ({data.crm.ventas})</button>
          </div>
          <div className="max-h-52 overflow-y-auto space-y-1">
            {detalle.map((v, i) => (
              <div key={i} className="flex items-center gap-2 text-[11px] border border-[var(--border)] rounded-[8px] px-2.5 py-1.5">
                <span className="text-[var(--text-muted)] shrink-0">{v.fecha}</span>
                {vista === 'erp' && (
                  <span className={`text-[8px] font-black uppercase px-1 py-0.5 rounded shrink-0 ${v.tipo_doc === 'envio' ? 'bg-amber-500/15 text-amber-600' : 'bg-emerald-500/15 text-emerald-600'}`}>
                    {v.empresa} · {v.tipo_doc === 'envio' ? 'Envío' : 'Fact.'}
                  </span>
                )}
                <span className="flex-1 truncate">{v.cliente}{v.cotizacion ? ` · ${v.cotizacion}` : ''}</span>
                <span className="font-bold shrink-0">{fmtQ(v.monto)}</span>
              </div>
            ))}
            {detalle.length === 0 && <p className="text-[10px] text-[var(--text-muted)] italic text-center py-3">Sin ventas en el período</p>}
          </div>
        </>
      )}
    </div>
  );
};
